import {
  createSlice,
  createAsyncThunk,
  type PayloadAction,
} from "@reduxjs/toolkit";
import {
  getMentorRecommendations as getMentorRecommendationsApi,
  mentorSessionRecommendations,
} from "../../api";
import type {
  FieldScore,
  MentorRecommendations,
  RecommendedFilters,
  RecommendedPage,
} from "../../shared/recommendationTypes";
import type { RootState } from "../../app/store";

export const PATHS_TO_RECOMMEND = [
  "/get-research-experience",
  "/explore-careers",
  "/find-graduate-schools",
  "/talk-to-an-expert",
  "/find-internships",
];

export const PAGE_FIELD_NAME_TO_PATH = {
  Research: "/get-research-experience",
  ExploreCareers: "/explore-careers",
  GradSchool: "/find-graduate-schools",
  Experts: "/talk-to-an-expert",
  Internships: "/find-internships",
};

export const DEGREE_OPTIONS = ["Masters", "Doctorate"];

export const SUBFIELD_OPTIONS = [
  "Brain Science and Cognitive Psychology",
  "Climate and Environmental Psychology",
  "Clinical Psychology",
  "Counseling Psychology",
  "Developmental Psychology",
  "Educational/School Psychology",
  "Experimental Psychology",
  "Forensic and Public Service Psychology",
  "Health Psychology",
  "Human Factors and Engineering Psychology",
  "Industrial and Organizational Psychology",
  "Quantitative Psychology",
  "Rehabilitation Psychology",
  "Social Psychology",
  "Sport and Performance Psychology",
];

function processPageRecommendations(
  fieldScores: FieldScore[],
): RecommendedPage[] {
  const topPages = [...fieldScores]
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)
    .filter((p) => p.score > 0);

  return topPages.map((page, index) => ({
    pageRoute:
      PAGE_FIELD_NAME_TO_PATH[
        page.field as keyof typeof PAGE_FIELD_NAME_TO_PATH
      ],
    seen: false,
    priority: index + 1,
  }));
}

function processRecommendedFilters(
  fieldScores: FieldScore[],
): RecommendedFilters {
  const recommendedSubFields: string[] = [];
  const recommendedDegrees: string[] = [];

  // Convert field scores to lowercase for case-insensitive matching
  const fieldNames = fieldScores.map((fs) => fs.field.toLowerCase());

  // Check if any SUBFIELD_OPTIONS appear in the field scores
  SUBFIELD_OPTIONS.forEach((subfield) => {
    if (fieldNames.some((field) => field.includes(subfield.toLowerCase()))) {
      recommendedSubFields.push(subfield);
    }
  });

  // Check if any DEGREE_OPTIONS appear in the field scores
  DEGREE_OPTIONS.forEach((degree) => {
    if (fieldNames.some((field) => field.includes(degree.toLowerCase()))) {
      recommendedDegrees.push(degree);
    }
  });

  return {
    subFields: recommendedSubFields,
    degrees: recommendedDegrees,
    adjusted: false,
  };
}

export const hydrateMentorRecommendations = createAsyncThunk<
  MentorRecommendations | null,
  string,
  { rejectValue: string }
>(
  "mentorRecommendations/hydrateMentorRecommendations",
  async (userId, thunkApi) => {
    try {
      const remoteRecommendations = await getMentorRecommendationsApi(userId);

      if (remoteRecommendations) return remoteRecommendations;
      return null;
    } catch (error) {
      return thunkApi.rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to hydrate mentor recommendations",
      );
    }
  },
);

export const generateMentorRecommendations = createAsyncThunk<
  MentorRecommendations,
  {
    userId: string;
    sessionId: string;
    timestampStart: string;
    timestampEnd?: string;
  },
  { rejectValue: string }
>(
  "mentorRecommendations/generateMentorRecommendations",
  async ({ userId, sessionId, timestampStart, timestampEnd }, thunkApi) => {
    try {
      const fieldScores = await mentorSessionRecommendations(
        userId,
        sessionId,
        10,
        timestampStart,
        timestampEnd,
      );

      return {
        recommendedPages: processPageRecommendations(
          fieldScores.pageFieldScores,
        ),
        recommendedFilters: processRecommendedFilters(
          fieldScores.subfieldScores,
        ),
      };
    } catch (error) {
      return thunkApi.rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to generate mentor recommendations",
      );
    }
  },
);

export const trackMentorRecommendationRouteVisit = createAsyncThunk<
  undefined,
  string,
  { state: RootState }
>(
  "mentorRecommendations/trackMentorRecommendationRouteVisit",
  (pathname, thunkApi) => {
    const recommendations = thunkApi.getState().mentorRecommendations.data;
    if (!recommendations) return;

    const normalizedPath = pathname.replace("/recommenderpage", "");
    if (!normalizedPath) return;

    const matchedRecommendation = recommendations.recommendedPages.find(
      (page) => page.pageRoute === normalizedPath,
    );

    if (!matchedRecommendation || matchedRecommendation.seen) {
      return;
    }

    thunkApi.dispatch(markRecommendedPageSeen(matchedRecommendation.pageRoute));
  },
);

interface MentorRecommendationsState {
  data: MentorRecommendations | null;
  status: "idle" | "pending" | "succeeded" | "failed";
  error: string | null;
}

const initialState: MentorRecommendationsState = {
  data: null,
  status: "idle",
  error: null,
};

const mentorRecommendationsSlice = createSlice({
  name: "mentorRecommendations",
  initialState,
  reducers: {
    clearMentorRecommendations: (state) => {
      state.data = null;
      state.error = null;
      state.status = "idle";
    },

    markRecommendedPageSeen: (state, action: PayloadAction<string>) => {
      if (!state.data) return;

      state.data.recommendedPages = state.data.recommendedPages.map((page) =>
        page.pageRoute === action.payload ? { ...page, seen: true } : page,
      );
    },

    markRecommendationFiltersAdjusted: (state) => {
      if (!state.data) return;

      state.data.recommendedFilters.adjusted = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(hydrateMentorRecommendations.pending, (state) => {
        state.status = "pending";
        state.error = null;
      })
      .addCase(hydrateMentorRecommendations.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = null;
        state.data = action.payload;
      })
      .addCase(hydrateMentorRecommendations.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ??
          action.error.message ??
          "Failed to hydrate mentor recommendations";
      })
      .addCase(generateMentorRecommendations.pending, (state) => {
        state.status = "pending";
        state.error = null;
      })
      .addCase(generateMentorRecommendations.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = null;
        state.data = action.payload;
      })
      .addCase(generateMentorRecommendations.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ??
          action.error.message ??
          "Failed to generate mentor recommendations";
      });
  },
});

export const {
  clearMentorRecommendations,
  markRecommendedPageSeen,
  markRecommendationFiltersAdjusted,
} = mentorRecommendationsSlice.actions;

export default mentorRecommendationsSlice.reducer;
